import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Download, RotateCcw } from 'lucide-react';
import { ProjectTypeAccordion } from './ProjectTypeAccordion';
import {
  getTemplatesForRoomType,
  getTemplateById,
  type ProjectTypeTemplate,
} from '@/components/widget/config/projectTypeTemplates';
import { ROOM_TYPES, getProjectTypes } from '@/components/widget/config/roomOptions';
import type { RoomType, RoomOverride, RoomConfig, ProjectTypeConfig } from '@/types/firestore';

interface AreaConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomType: RoomType;
  roomConfig: RoomConfig | undefined;
  onSave: (override: RoomOverride) => void;
}

export function AreaConfigModal({
  open,
  onOpenChange,
  roomType,
  roomConfig,
  onSave,
}: AreaConfigModalProps) {
  const [projectTypes, setProjectTypes] = useState<ProjectTypeConfig[]>([]);
  const [labelOverride, setLabelOverride] = useState('');
  const [descriptionOverride, setDescriptionOverride] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [addProjectTypeDialogOpen, setAddProjectTypeDialogOpen] = useState(false);
  const [newProjectTypeForm, setNewProjectTypeForm] = useState({ id: '', label: '', description: '' });

  const roomInfo = ROOM_TYPES.find((r) => r.id === roomType);
  const availableTemplates = getTemplatesForRoomType(roomType);
  const currentOverride = roomConfig?.room_overrides?.[roomType];

  // Initialize state when modal opens
  useEffect(() => {
    if (open) {
      // Load existing config or defaults
      const existingProjectTypes = getProjectTypes(roomType, roomConfig);
      setProjectTypes(existingProjectTypes ? [...existingProjectTypes] : []);
      setLabelOverride(currentOverride?.label || '');
      setDescriptionOverride(currentOverride?.description || '');
      setSelectedTemplate('');
      setHasChanges(false);
    }
  }, [open, roomType, roomConfig, currentOverride]);

  // Track changes
  useEffect(() => {
    if (!open) return;
    const existingProjectTypes = getProjectTypes(roomType, roomConfig);
    const existingJson = JSON.stringify(existingProjectTypes || []);
    const currentJson = JSON.stringify(projectTypes);
    const ptChanged = existingJson !== currentJson;
    const labelChanged = labelOverride !== (currentOverride?.label || '');
    const descChanged = descriptionOverride !== (currentOverride?.description || '');
    setHasChanges(ptChanged || labelChanged || descChanged);
  }, [projectTypes, labelOverride, descriptionOverride, roomType, roomConfig, currentOverride, open]);

  // Load template
  const loadTemplate = (templateId: string) => {
    const template = getTemplateById(templateId);
    if (template) {
      setProjectTypes([...template.projectTypes]);
      setSelectedTemplate(templateId);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    const defaults = getProjectTypes(roomType, undefined); // Get defaults without overrides
    setProjectTypes(defaults ? [...defaults] : []);
    setLabelOverride('');
    setDescriptionOverride('');
    setSelectedTemplate('');
  };

  // Add new project type
  const addNewProjectType = () => {
    if (!newProjectTypeForm.label.trim()) return;

    const id =
      newProjectTypeForm.id.trim() ||
      newProjectTypeForm.label
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');

    const newPt: ProjectTypeConfig = {
      id,
      label: newProjectTypeForm.label.trim(),
      description: newProjectTypeForm.description.trim() || undefined,
      selectionCategories: [],
    };

    setProjectTypes([...projectTypes, newPt]);
    setAddProjectTypeDialogOpen(false);
    setNewProjectTypeForm({ id: '', label: '', description: '' });
  };

  // Save changes
  const handleSave = () => {
    const override: RoomOverride = {};

    if (labelOverride.trim()) {
      override.label = labelOverride.trim();
    }
    if (descriptionOverride.trim()) {
      override.description = descriptionOverride.trim();
    }
    if (projectTypes.length > 0) {
      override.projectTypes = projectTypes;
    }

    onSave(override);
    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Configure: {currentOverride?.label || roomInfo?.label || roomType}
            </DialogTitle>
            <DialogDescription>
              Set up project types and selection categories for this area
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
            {/* Label & Description Overrides */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="area-label">Custom Label</Label>
                <Input
                  id="area-label"
                  placeholder={roomInfo?.label || 'Area name'}
                  value={labelOverride}
                  onChange={(e) => setLabelOverride(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="area-desc">Custom Description</Label>
                <Input
                  id="area-desc"
                  placeholder={roomInfo?.description || 'Description'}
                  value={descriptionOverride}
                  onChange={(e) => setDescriptionOverride(e.target.value)}
                />
              </div>
            </div>

            {/* Template & Actions Bar */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              {availableTemplates.length > 0 && (
                <Select value={selectedTemplate} onValueChange={loadTemplate}>
                  <SelectTrigger className="w-[200px]">
                    <Download className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Load Template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex flex-col">
                          <span>{template.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {template.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => setAddProjectTypeDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add Project Type
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="gap-1 ml-auto"
                onClick={resetToDefaults}
              >
                <RotateCcw className="h-4 w-4" />
                Reset to Defaults
              </Button>
            </div>

            {/* Project Types List */}
            <div className="flex-1 min-h-0">
              {projectTypes.length > 0 ? (
                <ProjectTypeAccordion
                  projectTypes={projectTypes}
                  onChange={setProjectTypes}
                  defaultOpenId={projectTypes[0]?.id}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center border rounded-lg bg-muted/30">
                  <p className="text-sm text-muted-foreground mb-4">
                    No project types configured for this area.
                    <br />
                    Add project types to allow customers to select specific renovation options.
                  </p>
                  <div className="flex gap-2">
                    {availableTemplates.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadTemplate(availableTemplates[0].id)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Load Template
                      </Button>
                    )}
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => setAddProjectTypeDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Project Type
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Project Type Dialog */}
      <Dialog open={addProjectTypeDialogOpen} onOpenChange={setAddProjectTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Project Type</DialogTitle>
            <DialogDescription>
              Create a new project type for {roomInfo?.label || roomType}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-pt-label">Label *</Label>
              <Input
                id="new-pt-label"
                placeholder="e.g., Deck, Man Cave, Home Theater"
                value={newProjectTypeForm.label}
                onChange={(e) =>
                  setNewProjectTypeForm((prev) => ({ ...prev, label: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pt-description">Description</Label>
              <Input
                id="new-pt-description"
                placeholder="Brief description of this project type"
                value={newProjectTypeForm.description}
                onChange={(e) =>
                  setNewProjectTypeForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-pt-id">ID (optional)</Label>
              <Input
                id="new-pt-id"
                placeholder="Auto-generated from label"
                value={newProjectTypeForm.id}
                onChange={(e) =>
                  setNewProjectTypeForm((prev) => ({ ...prev, id: e.target.value }))
                }
              />
              <p className="text-xs text-muted-foreground">
                Used internally. Leave empty to auto-generate.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddProjectTypeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={addNewProjectType}
              disabled={!newProjectTypeForm.label.trim()}
            >
              Add Project Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
